import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';

import {environment} from '@environments/environment';
import {ChannelType, slugifyRoleCode} from '@app/models';
import {AccountService} from '@app/services/account.service';

/**
 * Cliente HTTP de roles y permisos (Módulo → Opción → Acción).
 * Solo arma URLs y dispara peticiones; el swap mock↔real lo decide el interceptor.
 */
@Injectable({providedIn: 'root'})
export class RolesPermissionsApiService {

  constructor(private http: HttpClient, private accountService: AccountService) {}

  // ---- Catálogo descriptivo ----

  getModules(channel?: ChannelType): Observable<any> {
    const qs = channel ? `?channel_type=${channel}` : '';
    return this.http.get<any>(
      `${environment.api.auth}/${this.accountService.language}/api/v1/authz/catalog/modules/${qs}`
    );
  }

  getOptions(channel?: ChannelType): Observable<any> {
    const qs = channel ? `?channel_type=${channel}` : '';
    return this.http.get<any>(
      `${environment.api.auth}/${this.accountService.language}/api/v1/authz/catalog/options/${qs}`
    );
  }

  getActions(): Observable<any> {
    return this.http.get<any>(
      `${environment.api.auth}/${this.accountService.language}/api/v1/authz/catalog/actions/`
    );
  }

  getLevels(): Observable<any> {
    return this.http.get<any>(
      `${environment.api.auth}/${this.accountService.language}/api/v1/authz/catalog/levels/`
    );
  }

  // ---- Roles ----

  getRoles(channel?: ChannelType, keyword?: string, page: number = 1): Observable<any> {
    // Endpoint legacy `accounts/roles/` con `?type=1|2` (Web=1, Móvil=2).
    // Sin `type` trae ambos canales.
    const params: string[] = [`page=${page}`];
    if (channel === 'WEB') {
      params.push(`type=1`);
    } else if (channel === 'MOBILE') {
      params.push(`type=2`);
    }
    if (keyword && keyword.trim()) {
      params.push(`keyword=${encodeURIComponent(keyword.trim())}`);
    }
    return this.http.get<any>(
      `${environment.api.auth}/${this.accountService.language}/api/v1/accounts/roles/?${params.join('&')}`
    );
  }

  getRole(id: number | string): Observable<any> {
    return this.http.get<any>(
      `${environment.api.auth}/${this.accountService.language}/api/v1/accounts/roles/${id}/`
    );
  }

  createRole(form: any, orgBlind: boolean = false): Observable<any> {
    // POST `accounts/roles/`. Mapeo: channel_type→type (1=WEB, 2=MOBILE), level_id→level.
    // El `code` lo provee el form ya slugificado; si no, fallback.
    const channelType = form.channel_type ?? form.channelType;
    const type = channelType === 'MOBILE' ? 2 : 1;
    const body = {
      name: form.name,
      code: form.code || slugifyRoleCode(form.name),
      type,
      level: form.level_id ?? form.levelId,
      role_boss: null,
      is_active: form.is_active ?? form.isActive ?? true,
      can_modify: false,
      permission_activity: [],
    };
    // Rol global: `org_manual` hace que el interceptor NO añada x-org-id.
    const options = orgBlind ? {headers: new HttpHeaders({org_manual: 'true'})} : {};
    return this.http.post<any>(
      `${environment.api.auth}/${this.accountService.language}/api/v1/accounts/roles/`,
      body,
      options
    );
  }

  patchRole(id: number | string, payload: any): Observable<any> {
    // PATCH `accounts/roles/{id}/` con los campos editables (name, type, level, is_active).
    // `code`: lo reenviamos tal cual para preservarlo — el back lo auto-regenera desde
    // `name` si no llega. Nunca lo modificamos en el front.
    const body: any = {};

    if (payload.name !== undefined) {
      body.name = payload.name;
    }

    if (payload.code !== undefined && payload.code !== null && payload.code !== '') {
      body.code = payload.code;
    }

    if (payload.type !== undefined) {
      body.type = payload.type;
    } else if (payload.channel_type !== undefined || payload.channelType !== undefined) {
      const ch = payload.channel_type ?? payload.channelType;
      body.type = ch === 'MOBILE' ? 2 : 1;
    }

    if (payload.level !== undefined) {
      body.level = payload.level;
    } else if (payload.level_id !== undefined || payload.levelId !== undefined) {
      body.level = payload.level_id ?? payload.levelId;
    }

    if (payload.is_active !== undefined) {
      body.is_active = payload.is_active;
    } else if (payload.isActive !== undefined) {
      body.is_active = payload.isActive;
    }

    return this.http.patch<any>(
      `${environment.api.auth}/${this.accountService.language}/api/v1/accounts/roles/${id}/`,
      body
    );
  }

  // ---- Permisos por rol ----

  getRolePermissions(id: number | string): Observable<any> {
    // GET `authz/roles/{id}/permissions/`.
    return this.http.get<any>(
      `${environment.api.auth}/${this.accountService.language}/api/v1/authz/roles/${id}/permissions/`
    );
  }

  putRolePermissions(id: number | string, permissions: any[]): Observable<any> {
    // PUT `authz/roles/{id}/permissions/` (ojo: `authz`, no `accounts` como el CRUD de roles).
    return this.http.put<any>(
      `${environment.api.auth}/${this.accountService.language}/api/v1/authz/roles/${id}/permissions/`,
      {permissions}
    );
  }
}
